
import { supabase } from "@/integrations/supabase/client";
import { logActivityEvent } from "@/services/activity/activityLogService";

/**
 * Check if a reminder was sent recently for an order
 * @param orderId - The order ID to check
 * @returns Promise<boolean> - True if a reminder was sent in the last 30 minutes
 */
export const checkRecentReminder = async (
  orderId: string
): Promise<boolean> => {
  try {
    const thirtyMinsAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from("order_reminders")
      .select("*")
      .eq("order_id", orderId)
      .gte("created_at", thirtyMinsAgo)
      .single();

    if (error && error.code !== "PGRST116") {
      // Not found is not an error in this context
      console.error("Error checking recent reminders:", error);
    }

    return !!data; // Return true if a reminder was found, false otherwise
  } catch (error) {
    console.error("Error in checkRecentReminder:", error);
    return false;
  }
};

/**
 * Create a reminder for an order
 * @param orderId - The order ID to create a reminder for
 * @param tableId - The table ID associated with the order
 * @param storeId - The store ID associated with the order
 * @param message - The reminder message
 * @returns Promise with success status
 */
export const createOrderReminder = async (
  orderId: string,
  tableId: string,
  storeId: string,
  message: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get table information first
    const { data: tableData, error: tableError } = await supabase
      .from("store_tables")
      .select("number, name")
      .eq("id", tableId)
      .single();

    if (tableError) {
      console.error("Error fetching table information:", tableError);
    }

    const tableNumber = tableData?.number;
    const tableName = tableData?.name;

    // Create the reminder
    const { error } = await supabase.from("order_reminders").insert({
      order_id: orderId,
      table_id: tableId,
      store_id: storeId,
      message,
    });

    if (error) {
      console.error("Error creating order reminder:", error);
      return { success: false, error: error.message };
    }

    // Log the activity using the activity log service
    try {
      await logActivityEvent({
        entity_type: "order_reminder",
        entity_id: orderId,
        user_name: "Customer",
        action: `Table ${tableNumber} (${tableName}) Reminder: ${message.substring(0, 30)}${message.length > 30 ? '...' : ''}`,
        store_id: storeId,
        details: {
          message,
          table_id: tableId,
          table_number: tableNumber,
          table_name: tableName
        }
      });
    } catch (activityError) {
      console.error("Error logging reminder activity:", activityError);
      // Continue even if activity logging fails
    }

    return { success: true };
  } catch (error) {
    console.error("Error in createOrderReminder:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error creating reminder",
    };
  }
};
